import React, { useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import moment from 'moment';
import { useAngularService } from 'helpers/get_angular_service';
import Table from '../../components/table';
import api from '../../../helpers/api';
import sources from '../../../helpers/sources';
import Input from '../../components/input';
import { useExtendState } from '../../../helpers/use_extend_state';
import notification from '../../../helpers/notification';
import CommsTabs from '../../components/comms_tabs';
import { Gym } from '../../types/gym';
import Select from '../../components/select';
import formatMessageType from 'helpers/format_message_type';
import { usePageSettings } from 'helpers/use_page_settings';
import ContactLink from '../../components/contact_link';
import dateFormatter from 'helpers/date_formatter';
import HtmlContainer from '../../components/html_container';
import Button from '../../components/button';
import DateInput from '../../components/date_input';
import Link from 'react/components/link';
import Banner from 'react/components/banner';

interface Sorting {
  [key: string]: 'asc' | 'desc';
}

interface Filters {
  search: string;
  channel?: string;
  types?: string;
  sent_after?: string;
  sent_before?: string;
}

const channelTypes = ['email', 'sms', 'push_notification'];

const defaultPageSettings = {
  page_size: 10,
};

const SentMessages = () => {
  const $rootScope = useAngularService('$rootScope');
  const $modal = useAngularService('$modal');
  const pageId = 'sent_messages_list';

  const loadingAnimation = useAngularService('loadingAnimation');
  const GTMService = useAngularService('GTMService');

  const gym: Gym = $rootScope.gym;

  const [filters, extendFilters] = useExtendState<Filters>({
    search: '',
    sent_after: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    sent_before: moment().format('YYYY-MM-DD'),
  });
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sorting, setSorting] = useState<Sorting>({ created_at: 'desc' });
  const { pageSettings, setPageSettings } = usePageSettings({
    page: pageId,
    defaultValue: defaultPageSettings,
  });

  const channels = useMemo(() => {
    return _.compact([
      ['email', 'Emails'],
      ['sms', 'SMSes'],
      gym.mobile_app_enabled && ['push_notification', 'Push Notifications'],
      ['SmsReplyLogEntry', 'SMS Replies'],
      ['SmsFailureLogEntry', 'Failed SMSes'],
      gym.mobile_app_enabled && ['PushNotificationFailureLogEntry', 'Failed Push Notifications'],
      ['ContactEmailUnsubscribeLogEntry', 'Email Unsubscribes'],
      ['ContactSmsUnsubscribeLogEntry', 'SMS Unsubscribes'],
    ]);
  }, [gym]);

  useEffect(() => {
    GTMService.trackPage('Messages - Sent Messages');
    if (!pageSettings) return;
    if (pageSettings.order_column_name && pageSettings.order_direction) {
      setSorting({ [pageSettings.order_column_name]: pageSettings.order_direction });
    } else {
      setSorting({});
    }
  }, [pageSettings]);

  const setPageSize = (count: string | number) => {
    setPageSettings({ page_size: +count });
  };

  const toggleSorting = (key, val) => {
    setPageSettings({
      order_column_name: key,
      order_direction: val,
    });
  };

  const getMessages = (page: number) => {
    if (!pageSettings || !sorting) return;
    loadingAnimation.show();
    setLoading(true);
    const url = sources.sentMessages(gym.id);
    const { types, ...filtersRest } = filters;

    api
      .get(url, {
        page,
        count: pageSettings.page_size,
        'types[]': types,
        filters: filtersRest,
        sorting,
      })
      .then(
        (data) => {
          setLoading(false);
          setMessages(data);
          loadingAnimation.hide();
        },
        () => {
          loadingAnimation.hide();
          notification.error('Request could not be completed. Please try again');
        },
      );
  };

  const showMessage = (message) => {
    $modal.open({
      template: require('templates/gyms/bulk_messages/show_message.html.slim'),
      resolve: {
        message: () => message,
      },
      controller: 'ShowMessageModalCtrl',
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Type',
        id: 'type',
        headerClass: 'col-md-1',
        Cell: ({ row }) => <div>{formatMessageType(row.original)}</div>,
      },
      {
        Header: 'Recipient',
        id: 'recipient',
        headerClass: 'col-md-4',
        Cell: ({ row }) => (
          <>
            <ContactLink
              name={row.original.contact_name}
              id={row.original.contact_id}
              type={row.original.contact_type}
              status={row.original.contact_status}
              archived={row.original.contact_archived}
            />
            {row.original.data.channel === 'push_notification' &&
              row.original.contact_id !== row.original.push_notification_device_owner_id && (
                <div className="margin-top-8">
                  sent to {row.original.push_notification_device_owner_name}'s device
                </div>
              )}
            {row.original.data.channel === 'email' ||
              (row.original.data.channel === 'sms' && (
                <div className="margin-top-8">
                  {row.original.data.channel === 'email'
                    ? row.original.contact_email
                    : row.original.contact_phone_number}
                </div>
              ))}
          </>
        ),
      },
      {
        Header: 'Sent',
        id: 'created_at',
        headerClass: 'col-md-1',
        sortable: true,
        Cell: ({ row }) => (
          <div>
            <div className="nowrap">{dateFormatter(row.original.created_at, 'date')} </div>
            <div className="nowrap">{dateFormatter(row.original.created_at, 'time')}</div>
          </div>
        ),
      },
      {
        Header: 'Message',
        id: 'message',
        headerClass: 'col-md-5',
        Cell: ({ row }) => (
          <div>
            {row.original.type === 'sms_reply_log_entry' ? (
              row.original.data.reply_body
            ) : (
              <HtmlContainer
                html={row.original.data.subject || row.original.data.content}
                limit={50}
              />
            )}
          </div>
        ),
      },
      {
        Header: '',
        id: 'actions',
        headerClass: 'col-md-1',
        Cell: ({ row }) => (
          <div className="text-right">
            <Button variant="icon--v2" onClick={() => showMessage(row.original.data)}>
              <i className="icon-preview" />
            </Button>
          </div>
        ),
      },
    ],
    [filters, sorting],
  );

  return (
    <section className="content-section">
      <header className="page-header">
        <CommsTabs tab="sent_messages" />
      </header>

      <div className="container container--no-padding-top">
        <main>
          <Banner
            variant="info"
            icon="pi-info-circle"
            stackedLayout
            description={
              <>
                This report shows <strong>all messages</strong> sent to contacts (incl. bulk,
                one-off and system-triggered messages). For more information about bulk messages,
                see the <Link route="gym.bulkMessages.list">Sent Bulk Messages</Link> report.
              </>
            }
            noMarginBottom
          />

          <div className="filters-wrapper filters-wrapper--row filters-wrapper--v2">
            <div>
              <Select
                options={channels}
                getLabel={(item) => item[1]}
                getValue={(item) => item[0]}
                value={filters.channel}
                onChange={(channel) => {
                  const val =
                    channelTypes.indexOf(channel as string) >= 0
                      ? { channel, types: undefined }
                      : { channel: undefined, types: channel || undefined };
                  extendFilters(val);
                }}
                allowEmptyOption
                emptyLabel="All types"
                size="sm"
                variant="v2"
              />
            </div>

            <div className="date-range date-range--v2">
              <DateInput
                value={filters.sent_after}
                placeholder="From"
                onChange={(sent_after) => extendFilters({ sent_after })}
                variant="36"
              />

              <DateInput
                value={filters.sent_before}
                placeholder="To"
                onChange={(sent_before) => extendFilters({ sent_before })}
                variant="36"
              />
            </div>
          </div>

          <div className="table-header">
            <Input
              value={filters.search}
              onChange={(search) => extendFilters({ search })}
              placeholder="Search by name, phone number, email"
            />
          </div>

          <Table
            columns={columns}
            data={messages}
            loading={loading}
            emptyText="No results"
            pageSize={pageSettings?.page_size}
            setPageSize={setPageSize}
            loadData={(page) => getMessages(page)}
            filters={filters}
            sorting={sorting}
            toggleSorting={toggleSorting}
            withSearchBar
            variant="v2"
          />
        </main>
      </div>
    </section>
  );
};

angular.module('gym').value('SentMessages', SentMessages);
